/**
 * First-touch traffic source detection.
 *
 * Webhooks used to report the page the form sat on as the "source", which told
 * us nothing about acquisition. This resolves where the visitor actually came
 * from (Google, Bing, Facebook, an ad click, a direct visit) and sticks the
 * first-touch value in sessionStorage so later in-site navigation cannot
 * overwrite it with an internal referrer.
 */

const STORAGE_KEY = "crs_traffic_source";
/** First-touch attribution is kept for 90 days, matching the ad-platform window. */
const TTL_MS = 90 * 24 * 60 * 60 * 1000;

export interface TrafficSource {
  /** Human-friendly channel, e.g. "Google Ads", "Bing Organic", "Facebook". */
  source: string;
  /** paid | organic | social | referral | direct | email */
  medium: string;
  /** Raw referring URL at first touch, if any. */
  referrer: string | null;
  /** Campaign name from utm_campaign, if any. */
  campaign: string | null;
  /** Keyword from utm_term, if any. */
  term: string | null;
  /** Ad/content variant from utm_content, if any. */
  content: string | null;
  /** Landing page URL for the first touch. */
  landingPage: string | null;
}


const HOST_MAP: Array<[RegExp, string, string]> = [
  [/(^|\.)google\./i, "Google", "organic"],
  [/(^|\.)bing\.com$/i, "Bing", "organic"],
  [/(^|\.)duckduckgo\.com$/i, "DuckDuckGo", "organic"],
  [/(^|\.)search\.yahoo\./i, "Yahoo", "organic"],
  [/(^|\.)ecosia\.org$/i, "Ecosia", "organic"],
  [/(^|\.)(facebook|fb)\.com$/i, "Facebook", "social"],
  [/(^|\.)instagram\.com$/i, "Instagram", "social"],
  [/(^|\.)linkedin\.com$/i, "LinkedIn", "social"],
  [/(^|\.)(twitter|x)\.com$/i, "X (Twitter)", "social"],
  [/(^|\.)tiktok\.com$/i, "TikTok", "social"],
  [/(^|\.)youtube\.com$/i, "YouTube", "social"],
  [/(^|\.)reddit\.com$/i, "Reddit", "social"],
  [/(^|\.)checkatrade\./i, "Checkatrade", "referral"],
  [/(^|\.)trustpilot\./i, "Trustpilot", "referral"],
  [/(^|\.)mail\.(google|yahoo)\./i, "Email", "email"],
  [/(^|\.)outlook\./i, "Email", "email"],
];

function titleise(value: string): string {
  return value
    .split(/[\s_-]+/)
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
}

function detect(): TrafficSource {
  const params = new URLSearchParams(window.location.search);
  const referrer = document.referrer || null;
  const campaign = params.get("utm_campaign");
  const term = params.get("utm_term");
  const content = params.get("utm_content");
  const landingPage = window.location.href;
  const base = { referrer, campaign, term, content, landingPage };

  const utmSource = params.get("utm_source");
  const utmMedium = params.get("utm_medium");

  // 1. Paid click identifiers are the most reliable signal.
  if (params.get("gclid") || params.get("gbraid") || params.get("wbraid")) {
    return { source: "Google Ads", medium: "paid", ...base };
  }
  if (params.get("msclkid")) {
    return { source: "Microsoft Ads (Bing)", medium: "paid", ...base };
  }
  if (params.get("fbclid")) {
    return { source: "Facebook Ads", medium: "paid", ...base };
  }
  if (params.get("ttclid")) {
    return { source: "TikTok Ads", medium: "paid", ...base };
  }

  // 2. Explicit UTM tagging.
  if (utmSource) {
    return {
      source: titleise(utmSource),
      medium: utmMedium ? utmMedium.toLowerCase() : "referral",
      ...base,
    };
  }

  // 3. Referrer host.
  if (referrer) {
    try {
      const host = new URL(referrer).hostname;
      if (host !== window.location.hostname) {
        for (const [re, name, medium] of HOST_MAP) {
          if (re.test(host)) return { source: name, medium, ...base };
        }
        return { source: host.replace(/^www\./, ""), medium: "referral", ...base };
      }
    } catch {
      /* fall through to direct */
    }
  }

  return { source: "Direct", medium: "direct", ...base };
}

interface StoredTraffic {
  value: TrafficSource;
  savedAt: number;
}

function readStored(): TrafficSource | null {
  // localStorage first (90-day first-touch window), then migrate any older
  // sessionStorage value so an in-flight visit keeps its original source.
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as StoredTraffic | TrafficSource;
      if (parsed && typeof parsed === "object" && "value" in parsed) {
        if (Date.now() - parsed.savedAt < TTL_MS) return parsed.value;
        return null;
      }
      return parsed as TrafficSource;
    }
  } catch {
    /* ignore */
  }
  try {
    const legacy = sessionStorage.getItem(STORAGE_KEY);
    if (legacy) {
      const value = JSON.parse(legacy) as TrafficSource;
      try {
        localStorage.setItem(
          STORAGE_KEY,
          JSON.stringify({ value, savedAt: Date.now() } satisfies StoredTraffic),
        );
      } catch {
        /* ignore */
      }
      return value;
    }
  } catch {
    /* ignore */
  }

  return null;
}

/** First-touch traffic source, sticky for 90 days across tabs and sessions. */
export function getTrafficSource(): TrafficSource {
  if (typeof window === "undefined") {
    return {
      source: "Unknown",
      medium: "unknown",
      referrer: null,
      campaign: null,
      term: null,
      content: null,
      landingPage: null,
    };
  }
  const stored = readStored();
  const value = stored ?? detect();
  if (!stored) {
    // Only stamp on first touch, so the 90-day window doesn't slide forever.
    try {
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({ value, savedAt: Date.now() } satisfies StoredTraffic),
      );
    } catch {
      /* ignore */
    }
  }
  return value;

}

